import { BestiaryCardData } from '../../utils/bestiary.utils';
import { ELEMENT_COLORS } from '../../data/elements.config';
import { EMOTION_NAMES } from '../../data/emotions.config';
import { entitiesConfig } from '../../data/entities.config';
import { Icon, frameExists } from './Icon';

// Bestiary portrait: monsters have no dedicated icon, so render the first frame
// of their idle animation (directional sheets use `-bottom-0`, others `-0`).
// Falls back to the config icon. Placeholder for now until real portraits.
function monsterPortraitSprite(configId: string): string | undefined {
  const sheet = entitiesConfig[configId]?.animations?.idle?.spriteTextureName;
  if (!sheet) {
    return undefined;
  }
  for (const candidate of [`${sheet}-bottom-0`, `${sheet}-0`]) {
    if (frameExists(candidate)) {
      return candidate;
    }
  }
  return undefined;
}

interface Props {
  card: BestiaryCardData;
  /** When set, the card is navigable (binder). Omit for the lightbox. */
  navRow?: number;
  navCol?: number;
  large?: boolean;
}

export const BestiaryCard = ({ card, navRow, navCol, large }: Props) => {
  const { config, loot } = card;
  const navProps =
    navRow !== undefined && navCol !== undefined
      ? {
          'data-nav-row': navRow,
          'data-nav-col': navCol,
          'data-nav-id': config.configId,
        }
      : {};

  return (
    <div
      className={`bestiary-card${large ? ' bestiary-card--large' : ''}`}
      {...navProps}
    >
      <div className="bestiary-card-top">
        <span className="bestiary-card-name">{config.label}</span>
      </div>

      <div className="bestiary-card-portrait">
        <div className="bestiary-card-portrait-frame">
          <Icon
            sprite={monsterPortraitSprite(config.configId) ?? config.icon}
            fill
          />
        </div>
      </div>

      <div className="bestiary-card-loot">
        {loot.map((l, i) => (
          <div
            key={`${l.itemId}-${i}`}
            className={`bestiary-loot-item${
              l.eliteOnly ? ' bestiary-loot-item--elite' : ''
            }`}
            title={l.label}
            {...(large
              ? {
                  'data-nav-row': 0,
                  'data-nav-col': i,
                  'data-nav-id': `loot:${i}`,
                }
              : {})}
          >
            <Icon
              sprite={l.sprite}
              overlay={l.overlay}
              size={large ? 28 : 18}
            />
          </div>
        ))}
      </div>

      <div className="bestiary-card-bottom">
        <span className="bestiary-card-number">
          Nº {String(config.bestiaryId).padStart(2, '0')}
        </span>

        <span className="bestiary-card-emotions">
          {(config.emotionTypes ?? []).map((e) => (
            <span
              key={e}
              className="bestiary-emotion-chip"
              style={{ backgroundColor: ELEMENT_COLORS[e] }}
            >
              {EMOTION_NAMES[e]}
            </span>
          ))}
        </span>
      </div>
    </div>
  );
};
